import React from 'react';
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import { withStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import TextareaAutosize from 'react-autosize-textarea';
import { withTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { emitRealtimeEvent } from '../../utils/realtimeEventEmitter';

const BRAND = '#00a7e1';
const BRAND_HOVER = '#0096cc';

const styles = theme => ({
  root: {
    width: "100%"
  },
  paper: {
    borderRadius: 11,
    boxShadow: theme.shadows[6],
    overflow: 'hidden',
    borderTop: `3px solid ${BRAND}`,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 96px)',
  },
  shell: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    maxHeight: 'calc(100vh - 96px)',
    overflow: 'hidden',
  },
  dialogHeader: {
    flexShrink: 0,
    padding: theme.spacing(2, 2.5, 1.5),
    borderBottom: '1px solid rgba(0, 167, 225, 0.2)',
    backgroundColor: '#fff',
  },
  headerTitle: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: 'rgba(0, 0, 0, 0.87)',
    lineHeight: 1.3,
  },
  titleAccent: {
    color: BRAND,
    fontWeight: 700,
    fontSize: '1.45rem',
    marginLeft: theme.spacing(1),
  },
  progress: {
    height: 3,
    backgroundColor: 'rgba(0, 167, 225, 0.15)',
    '& .MuiLinearProgress-bar': {
      backgroundColor: BRAND,
    },
  },
  dialogBody: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: theme.spacing(1.5, 2.5, 1),
    backgroundColor: '#fff',
  },
  notesSection: {
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: 'rgba(0, 167, 225, 0.05)',
    borderRadius: 8,
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.25),
    border: '1px solid rgba(0, 167, 225, 0.18)',
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: BRAND,
    marginBottom: theme.spacing(1),
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  notesTextarea: {
    minHeight: 72,
    maxHeight: 120,
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid rgba(0, 167, 225, 0.35)',
    borderRadius: 8,
    fontSize: '0.875rem',
    padding: theme.spacing(1.25, 1.5),
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    color: 'rgba(0, 0, 0, 0.87)',
    resize: 'vertical',
    backgroundColor: '#fff',
    '&:focus': {
      outline: 'none',
      borderColor: BRAND,
      boxShadow: '0 0 0 2px rgba(0, 167, 225, 0.25)',
    },
  },
  gridSection: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  gridHost: {
    width: '100%',
    height: 'clamp(180px, calc(100vh - 430px), 360px)',
    '& .ag-root-wrapper': {
      borderRadius: 8,
      borderColor: 'rgba(0, 167, 225, 0.28)',
    },
    '& .ag-header': {
      backgroundColor: 'rgba(0, 167, 225, 0.1)',
    },
    '& .ag-header-cell-text': {
      color: BRAND,
      fontWeight: 700,
      letterSpacing: '0.04em',
      fontSize: '0.72rem',
    },
  },
  loaderWrap: {
    flex: '1 1 auto',
    minHeight: 220,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    color: 'rgba(0, 0, 0, 0.6)',
  },
  dialogFooter: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 2.5),
    borderTop: '1px solid rgba(0, 167, 225, 0.2)',
    backgroundColor: 'rgba(0, 167, 225, 0.06)',
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    marginLeft: 'auto',
  },
  primaryButton: {
    backgroundColor: BRAND,
    color: '#fff',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.875, 2.25),
    borderRadius: 8,
    minWidth: 130,
    fontSize: '0.875rem',
    boxShadow: '0 1px 3px rgba(0, 167, 225, 0.35)',
    '&:hover': {
      backgroundColor: BRAND_HOVER,
      color: '#fff',
    },
    '&:disabled': {
      color: 'rgba(0, 0, 0, 0.38)',
      backgroundColor: '#E4E5E8',
      boxShadow: 'none',
    },
  },
  secondaryButton: {
    color: BRAND,
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.875, 2),
    borderRadius: 8,
    minWidth: 90,
    fontSize: '0.875rem',
    border: '1px solid rgba(0, 167, 225, 0.55)',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(0, 167, 225, 0.08)',
      borderColor: BRAND,
    },
    '&:disabled': {
      color: 'rgba(0, 0, 0, 0.38)',
      borderColor: 'rgba(0, 0, 0, 0.12)',
    },
  },
  confirmPaper: {
    borderRadius: 11,
    boxShadow: theme.shadows[6],
    borderTop: `3px solid ${BRAND}`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  confirmBody: {
    padding: theme.spacing(2.5, 2.5, 1.5),
    textAlign: 'center',
  },
  confirmText: {
    fontSize: '0.875rem',
    color: 'rgba(0, 0, 0, 0.75)',
    lineHeight: 1.5,
  },
  confirmFooter: {
    justifyContent: 'center',
  },
});

const defaultColDef = {
  sortable: true,
  resizable: true,
  suppressMenu: true,
  flex: 1,
  minWidth: 80,
};

class Manualconsultation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      DialogOpen: this.props.detailDialogOpen,
      prescriptionRows: [],
      consultnotes: '',
      ReleaseOrder: false,
      ReleaseOrderOpen: false,
      orderNotReleasedOpen: false,
      UserId: '',
      UserName: '',
      isLoading: true,
      isSaving: false,
      savingMode: null,
    };
    this.gridApi = null;
  }

  componentDidMount() {
    this.loadConsult();
  }

  authHeaders = () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    return {
      Authorization: 'Bearer ' + user.signInUserSession.idToken.jwtToken,
      'Content-Type': 'application/json',
    };
  };

  notify = (key) => {
    if (this._unmounted) {
      return;
    }
    toast.error(this.props.t(key));
  };

  safeSetState = (update, callback) => {
    if (this._unmounted) {
      return;
    }
    this.setState(update, callback);
  };

  readBody = async (response) => {
    let text = '';
    try {
      text = await response.text();
    } catch (error) {
      text = '';
    }
    if (!text) {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (error) {
      return { success: false };
    }
  };

  isFailurePayload = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return false;
    }
    if (data.success === false) {
      return true;
    }
    return typeof data.stack === 'string' && data.stack.length > 0;
  };

  loadConsult = () => {
    this.setState({ isLoading: true });
    Promise.all([
      this.getPrescriptionDetailByPickupCode(this.props.pickupCode),
      this.getManualReleaseOrderDetailByPickupCode(this.props.pickupCode),
      this.loadCurrentUser(),
    ]).finally(() => {
      if (this._unmounted) {
        return;
      }
      this.setState({ isLoading: false });
    });
  };

  componentWillUnmount() {
    this._unmounted = true;
  }

  loadCurrentUser = () => {
    return Promise.resolve()
      .then(() => {
        const sessionUser = JSON.parse(sessionStorage.getItem('user'));
        return fetch(process.env.REACT_APP_BASEURL + 'users/' + sessionUser.username, {
          method: 'GET',
          withCredentials: true,
          headers: this.authHeaders(),
        });
      })
      .then((response) => this.readBody(response).then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        const user = Array.isArray(data) ? data[0] : null;
        if (!ok || !user || this.isFailurePayload(data)) {
          this.notify('Toast.Error.ConsultLoadUser');
          return;
        }
        this.safeSetState({ UserId: user.id, UserName: user.name });
      })
      .catch(() => {
        this.notify('Toast.Error.ConsultLoadUser');
      });
  };

  getPrescriptionDetailByPickupCode = (pickupcode) => {
    return Promise.resolve().then(() => fetch(process.env.REACT_APP_BASEURL + 'getprescriptiondetailbypucode/' + pickupcode, {
      method: 'GET',
      withCredentials: true,
      headers: this.authHeaders(),
    }))
      .then((response) => this.readBody(response).then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || !Array.isArray(data)) {
          this.safeSetState({ prescriptionRows: [] });
          this.notify('Toast.Error.ConsultLoadItems');
          return;
        }
        this.safeSetState({
          prescriptionRows: data.map((row, index) => ({
            ...row,
            _rowId: `${row.rx_no || 'rx'}-${index}`,
          })),
        });
      })
      .catch(() => {
        this.safeSetState({ prescriptionRows: [] });
        this.notify('Toast.Error.ConsultLoadItems');
      });
  };

  getManualReleaseOrderDetailByPickupCode = (pickupcode) => {
    return Promise.resolve().then(() => fetch(process.env.REACT_APP_BASEURL + 'getmanualreleaseorderdetailbypickupcode/' + pickupcode, {
      method: 'GET',
      withCredentials: true,
      headers: this.authHeaders(),
    }))
      .then((response) => this.readBody(response).then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!data) {
          return;
        }
        if (!ok || this.isFailurePayload(data)) {
          this.notify('Toast.Error.ConsultLoadNotes');
          return;
        }
        const item = Array.isArray(data) ? data[0] : data;
        if (!item) {
          return;
        }
        this.safeSetState({
          ReleaseOrder: !!item.is_releaseorder,
          consultnotes: item.consultation_notes || '',
        });
      })
      .catch(() => {
        this.notify('Toast.Error.ConsultLoadNotes');
      });
  };

  handleClose = () => {
    if (this.state.isSaving) {
      return;
    }
    this.setState({ DialogOpen: false, orderNotReleasedOpen: false, ReleaseOrderOpen: false });
    this.props.onPopupClose();
  };

  handleChangeConsultNotes = event => {
    event.preventDefault && event.preventDefault();
    this.setState({ consultnotes: event.target.value });
  };

  handleReleaseOrder = () => {
    if (this.state.isSaving || this.state.ReleaseOrder) {
      return;
    }
    this.setState({ ReleaseOrderOpen: true });
  };

  releaseorderaccept = () => {
    this.savemanualuserConsult(true);
  };

  handleCloseRleaseOrder = () => {
    if (this.state.isSaving) {
      return;
    }
    this.setState({ ReleaseOrderOpen: false });
  };

  handleConsultClose = () => {
    if (this.state.isSaving) {
      return;
    }
    this.setState({ orderNotReleasedOpen: false });
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  };

  onGridSizeChanged = (params) => {
    params.api.sizeColumnsToFit();
  };

  getColumnDefs = () => {
    const { t, i18n } = this.props;
    const lang = i18n && i18n.language;
    if (this._columnDefs && this._columnDefsLang === lang) {
      return this._columnDefs;
    }
    this._columnDefsLang = lang;
    this._columnDefs = [
      {
        field: 'rx_no',
        headerName: t("Assign.ManualConsult.RxHash"),
        minWidth: 110,
        flex: 0.8,
      },
      {
        field: 'drug_name',
        headerName: t("Assign.ManualConsult.DrugName"),
        minWidth: 180,
        flex: 2,
      },
      {
        field: 'doseage_form',
        headerName: t("Assign.ManualConsult.Dose"),
        minWidth: 90,
        flex: 0.7,
      },
      {
        field: 'qty',
        headerName: t("Assign.ManualConsult.Qty"),
        minWidth: 70,
        maxWidth: 110,
        flex: 0.4,
        type: 'rightAligned',
      },
    ];
    return this._columnDefs;
  };

  savemanualuserConsult = (releaseOrder = this.state.ReleaseOrder, savingMode) => {
    if (this.state.isSaving) {
      return;
    }
    const searchedRxItems = this.props.searchedRxItems;
    const isConsultRequired = Array.isArray(searchedRxItems)
      && searchedRxItems.some((item) => item && item.is_consultrequire === true);
    const selectedObject = this.props.selectedObject || {};
    const accountDetails = this.props.accountDetails || {};
    const pharmacyDetails = this.props.pharmacyDetails || {};
    const statusObj = {
      consult_start_date: new Date(),
      consult_end_date: new Date(),
      pickup_code: this.props.pickupCode,
      user_name: (selectedObject.FirstName || '') + (selectedObject.LastName || ''),
      call_duration: '00:00:00',
      user_id: this.state.UserId,
      consultation_name: this.state.UserName,
      Status: 'Offline',
      accounts_id: accountDetails.id,
      store_name: accountDetails.name,
      box_id: this.props.boxId,
      kiosk_name: pharmacyDetails.name,
      consultation_notes: this.state.consultnotes,
      is_releaseorder: releaseOrder,
      isConsultRequired,
    };
    const mode = savingMode || (releaseOrder ? 'release' : 'complete');
    this.safeSetState({ isSaving: true, savingMode: mode });
    Promise.resolve()
      .then(() => fetch(process.env.REACT_APP_BASEURL + 'saveuserconsult', {
        method: 'post',
        withCredentials: true,
        headers: this.authHeaders(),
        body: JSON.stringify(statusObj),
      }))
      .then((response) => this.readBody(response).then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || this.isFailurePayload(data) || !(data && data.user_consult_master && data.user_consult_master.user_consult_master_id)) {
          throw new Error('save failed');
        }
        if (releaseOrder) {
          emitRealtimeEvent('isOrderReleasedFromDeliveryPrescription', this.props.pickupCode);
          toast.success(this.props.t('Toast.Success.OrderReleased'));
        } else {
          toast.success(this.props.t('Toast.Success.ConsultComplete'));
        }
        this.safeSetState({
          ReleaseOrder: releaseOrder,
          ReleaseOrderOpen: false,
          orderNotReleasedOpen: false,
          isSaving: false,
          savingMode: null,
          DialogOpen: false,
        }, () => {
          if (this.props.onPopupClose) {
            this.props.onPopupClose();
          }
        });
      })
      .catch(() => {
        this.safeSetState({ isSaving: false, savingMode: null });
        this.notify(releaseOrder ? 'Toast.Error.ConsultRelease' : 'Toast.Error.ConsultSave');
      });
  };

  renderButtonLabel = (mode, label, spinnerColor = '#fff') => {
    const showSpinner = this.state.isSaving && this.state.savingMode === mode;
    return (
      <React.Fragment>
        {showSpinner ? (
          <CircularProgress
            size={16}
            thickness={5}
            style={{ color: spinnerColor, marginRight: 8 }}
          />
        ) : null}
        {label}
      </React.Fragment>
    );
  };

  render() {
    const { classes, t } = this.props;
    const { isLoading, isSaving, prescriptionRows } = this.state;
    return (
      <div className={classes.root}>
        <Dialog
          open={this.state.DialogOpen || false}
          onClose={this.handleClose}
          aria-labelledby="consult-dialog-title"
          aria-describedby="consult-dialog-description"
          fullWidth
          maxWidth="md"
          PaperProps={{
            className: classes.paper,
            style: {
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 96px)',
            },
          }}
        >
          <div className={classes.shell}>
            <div className={classes.dialogHeader}>
              <Typography id="consult-dialog-title" component="h2" className={classes.headerTitle}>
                {t("Assign.ManualConsult.ConsultRoom")}
                <span className={classes.titleAccent}>{this.props.pickupCode}</span>
              </Typography>
            </div>
            {(isLoading || isSaving) ? <LinearProgress className={classes.progress} /> : null}

            <div className={classes.dialogBody} id="consult-dialog-description">
              {isLoading ? (
                <div className={classes.loaderWrap}>
                  <CircularProgress size={36} thickness={4} sx={{ color: BRAND }} />
                  <Typography variant="body2">{t("Assign.ManualConsult.Loading")}</Typography>
                </div>
              ) : (
                <React.Fragment>
                  <div className={classes.notesSection}>
                    <Typography variant="subtitle2" className={classes.sectionTitle}>
                      {t("Assign.ManualConsult.ConsultNotes")}
                    </Typography>
                    <TextareaAutosize
                      className={classes.notesTextarea}
                      onBlur={this.handleChangeConsultNotes}
                      onChange={this.handleChangeConsultNotes}
                      defaultValue={this.state.consultnotes}
                      value={this.state.consultnotes}
                      placeholder={t("Assign.ManualConsult.ConsultNotes")}
                      disabled={isSaving}
                    />
                  </div>
                  <div className={classes.gridSection}>
                    <Typography variant="subtitle2" className={classes.sectionTitle}>
                      {t("Assign.ManualConsult.PrescriptionItems")}
                    </Typography>
                    <div className={`${classes.gridHost} ag-theme-alpine`}>
                      <AgGridReact
                        rowData={prescriptionRows}
                        columnDefs={this.getColumnDefs()}
                        defaultColDef={defaultColDef}
                        getRowId={(params) => params.data._rowId}
                        headerHeight={38}
                        rowHeight={40}
                        animateRows={false}
                        suppressCellFocus
                        overlayNoRowsTemplate={`<span>${t("Assign.ManualConsult.NoItemsToDisplay")}</span>`}
                        onGridReady={this.onGridReady}
                        onGridSizeChanged={this.onGridSizeChanged}
                        onFirstDataRendered={this.onGridSizeChanged}
                      />
                    </div>
                  </div>
                </React.Fragment>
              )}
            </div>

            <div className={classes.dialogFooter}>
              <FormControlLabel
                label={t("Assign.ManualConsult.ReleaseOrder")}
                control={
                  <Checkbox
                    disabled={this.state.ReleaseOrder || isSaving || isLoading}
                    checked={this.state.ReleaseOrder}
                    onChange={this.handleReleaseOrder}
                    value={this.state.ReleaseOrder}
                    size="small"
                    sx={{
                      color: BRAND,
                      '&.Mui-checked': { color: BRAND },
                    }}
                  />
                }
                sx={{ marginLeft: 0, marginRight: 0, color: 'rgba(0, 0, 0, 0.87)' }}
              />
              <div className={classes.footerActions}>
                <Button
                  onClick={this.handleClose}
                  className={classes.secondaryButton}
                  disabled={isSaving}
                >
                  {t("Assign.ManualConsult.Close")}
                </Button>
                <Button
                  type="button"
                  disabled={isSaving || isLoading}
                  className={classes.primaryButton}
                  variant="contained"
                  onClick={
                    this.state.ReleaseOrder
                      ? () => this.savemanualuserConsult(true, 'complete')
                      : () => this.setState({ orderNotReleasedOpen: true })
                  }
                >
                  {this.renderButtonLabel('complete', t("Assign.ManualConsult.CompleteConsult"))}
                </Button>
              </div>
            </div>
          </div>
        </Dialog>

        <Dialog
          open={this.state.ReleaseOrderOpen}
          onClose={this.handleCloseRleaseOrder}
          aria-labelledby="release-confirm-title"
          aria-describedby="release-confirm-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{ className: classes.confirmPaper }}
        >
          <div className={classes.dialogHeader}>
            <Typography id="release-confirm-title" component="h2" className={classes.headerTitle}>
              {t("Assign.ManualConsult.ReleaseOrderTitle")}
            </Typography>
          </div>
          {isSaving ? <LinearProgress className={classes.progress} /> : null}
          <div className={classes.confirmBody}>
            <Typography id="release-confirm-description" className={classes.confirmText}>
              {t("Assign.ManualConsult.AreYouSureToReleaseOrder")}
            </Typography>
          </div>
          <div className={`${classes.dialogFooter} ${classes.confirmFooter}`}>
            <Button
              className={classes.secondaryButton}
              disabled={isSaving}
              onClick={this.handleCloseRleaseOrder}
            >
              {t("Assign.ManualConsult.No")}
            </Button>
            <Button
              className={classes.primaryButton}
              variant="contained"
              disabled={isSaving}
              onClick={this.releaseorderaccept}
            >
              {this.renderButtonLabel('release', t("Assign.ManualConsult.Yes"))}
            </Button>
          </div>
        </Dialog>

        <Dialog
          open={this.state.orderNotReleasedOpen}
          onClose={this.handleConsultClose}
          aria-labelledby="order-not-released-title"
          aria-describedby="order-not-released-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{ className: classes.confirmPaper }}
        >
          <div className={classes.dialogHeader}>
            <Typography id="order-not-released-title" component="h2" className={classes.headerTitle}>
              {t("Assign.ManualConsult.CompleteConsultTitle")}
            </Typography>
          </div>
          {isSaving ? <LinearProgress className={classes.progress} /> : null}
          <div className={classes.confirmBody}>
            <Typography id="order-not-released-description" className={classes.confirmText}>
              {t("Assign.ManualConsult.ConfirmToReleaseOrder")}
            </Typography>
          </div>
          <div className={`${classes.dialogFooter} ${classes.confirmFooter}`}>
            <Button
              className={classes.primaryButton}
              variant="contained"
              disabled={isSaving}
              onClick={this.releaseorderaccept}
            >
              {this.renderButtonLabel('release', t("Assign.ManualConsult.ReleaseOrder"))}
            </Button>
            <Button
              disabled={isSaving}
              className={classes.secondaryButton}
              onClick={() => this.savemanualuserConsult(false, 'complete')}
            >
              {this.renderButtonLabel('complete', t("Assign.ManualConsult.CompleteConsult"), BRAND)}
            </Button>
          </div>
        </Dialog>
      </div>
    );
  }
}

Manualconsultation.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles, { withTheme: true })(withTranslation()(Manualconsultation));



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/Manual-consultation.js